import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileSpreadsheet, Loader2 } from 'lucide-react';
import { downloadBlob } from './PaymentsImportPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const schoolYears = () => {
  const now = new Date();
  const y = now.getMonth() >= 8 ? now.getFullYear() : now.getFullYear() - 1;
  return [y, y - 1, y - 2];
};

const presetRange = (preset, y) => ({
  YEAR: [`${y}-09-01`, `${y + 1}-08-31`],
  T1: [`${y}-09-01`, `${y}-12-31`],
  T2: [`${y + 1}-01-01`, `${y + 1}-03-31`],
  T3: [`${y + 1}-04-01`, `${y + 1}-06-30`],
}[preset]);

const FilterSelect = ({ label, value, onChange, testId, children }) => (
  <div className="space-y-1">
    <Label className="text-xs text-gray-600">{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>{children}</SelectContent>
    </Select>
  </div>
);

export const FinanceExportPanel = () => {
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState('ALL');
  const [status, setStatus] = useState('ALL');
  const [year, setYear] = useState(String(schoolYears()[0]));
  const [preset, setPreset] = useState('CUSTOM');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/classes`).then((r) => setClasses(r.data)).catch(() => {});
  }, []);

  const applyPreset = (p, y = year) => {
    setPreset(p);
    if (p !== 'CUSTOM') {
      const [s, e] = presetRange(p, Number(y));
      setStart(s);
      setEnd(e);
    }
  };

  const exportFile = async () => {
    setBusy(true);
    try {
      const params = {};
      if (classId !== 'ALL') params.class_id = classId;
      if (status !== 'ALL') params.status = status;
      if (start) params.start = start;
      if (end) params.end = end;
      const res = await axios.get(`${API}/finance/export`, { params, responseType: 'blob' });
      downloadBlob(res.data, `export_finances_${start || 'origine'}_${end || new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success('Export Excel téléchargé');
    } catch (e) {
      let msg = 'Export impossible';
      try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch (_) { /* réponse non JSON */ }
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="finance-export-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-green-700" /> Export Excel</CardTitle>
        <CardDescription>Factures et paiements dans un classeur (onglets Factures, Paiements, Filtres). La période porte sur l’échéance des factures et la date des paiements.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FilterSelect label="Classe" value={classId} onChange={setClassId} testId="export-class-select">
            <SelectItem value="ALL">Toutes les classes</SelectItem>
            {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </FilterSelect>
          <FilterSelect label="Statut" value={status} onChange={setStatus} testId="export-status-select">
            <SelectItem value="ALL">Tous les statuts</SelectItem>
            <SelectItem value="OVERDUE">En retard</SelectItem>
            <SelectItem value="PARTIAL">Partiellement payées</SelectItem>
            <SelectItem value="PENDING">Non échues</SelectItem>
            <SelectItem value="PAID">Payées</SelectItem>
            <SelectItem value="CANCELLED">Annulées</SelectItem>
          </FilterSelect>
          <FilterSelect label="Année scolaire" value={year} onChange={(y) => { setYear(y); if (preset !== 'CUSTOM') applyPreset(preset, y); }} testId="export-year-select">
            {schoolYears().map((y) => <SelectItem key={y} value={String(y)}>{y}-{y + 1}</SelectItem>)}
          </FilterSelect>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FilterSelect label="Période" value={preset} onChange={(p) => applyPreset(p)} testId="export-period-select">
            <SelectItem value="CUSTOM">Dates libres</SelectItem>
            <SelectItem value="YEAR">Année scolaire entière</SelectItem>
            <SelectItem value="T1">1er trimestre (sept.–déc.)</SelectItem>
            <SelectItem value="T2">2e trimestre (janv.–mars)</SelectItem>
            <SelectItem value="T3">3e trimestre (avr.–juin)</SelectItem>
          </FilterSelect>
          <div className="space-y-1">
            <Label className="text-xs text-gray-600">Du</Label>
            <Input type="date" lang="fr" value={start} onChange={(e) => { setStart(e.target.value); setPreset('CUSTOM'); }} data-testid="export-start-date" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-gray-600">Au</Label>
            <Input type="date" lang="fr" value={end} onChange={(e) => { setEnd(e.target.value); setPreset('CUSTOM'); }} data-testid="export-end-date" />
          </div>
        </div>
        <Button onClick={exportFile} disabled={busy} className="bg-green-700 hover:bg-green-800" data-testid="export-excel-btn">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
          Exporter en Excel
        </Button>
      </CardContent>
    </Card>
  );
};

export default FinanceExportPanel;
